import { browserAuthHeaders } from './browserAuthHeaders'
/**
 * Realtime (Laravel Reverb) bootstrap.
 *
 * Creates the single Laravel Echo client and publishes it on `window.Echo`,
 * which is the contract every realtime hook (`useEcho`, `getEcho`) reads.
 * Without this module nothing ever instantiates Echo and all realtime
 * features silently no-op.
 *
 * Auth: the backend broadcast route (`POST /broadcasting/auth`) authenticates
 * a Passport bearer token and resolves the tenant from `X-Tenant-ID`, so the
 * same headers the axios client sends are attached to Echo's auth requests.
 */
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import { getAccessToken, getTenantId, getTokenExpiry } from '@/services/tokenStorage'
import { REALTIME_FEATURES } from '@/config/featureFlags'

declare global {
  interface Window {
    Echo: any
    Pusher?: any
  }
}

// `${token}:${tenantId}` the current client was built for; lets login /
// tenant switches rebuild the connection with fresh credentials.
let bootstrappedIdentity: string | null = null

// Monotonic id of the live Echo instance. Rebuilding the client (login,
// tenant switch, post-logout re-init) bumps it; module-level subscribers
// (channel registry consumers) compare against it to detect that the Echo
// they last saw is gone. Null = no live instance.
let instanceSeq = 0
let currentInstanceId: number | null = null

/**
 * Id of the Echo instance `initEcho` last built, or null when no live
 * instance exists. Lets non-component code (e.g. singleton hooks) detect
 * Echo rebuilds without holding a reference to the instance itself.
 */
export function getEchoInstanceId(): number | null {
  return currentInstanceId
}

/**
 * Channel listener registry.
 *
 * initEcho rebuilds the Echo instance on every identity (token+tenant)
 * change — orphaning every `echo.private(...).listen(...)` subscription the
 * previous instance carried. Registrations made through
 * `registerEchoListener` are REPLAYED onto each new instance (at init time
 * and again on its 'connected' event), so a socket rebuild no longer
 * silently kills live listeners.
 */
interface EchoRegistration {
  channelName: string
  eventName: string
  handler: (payload: any) => void
}

const echoRegistrations: EchoRegistration[] = []
// Registrations already applied to a given Echo instance — `listen` binds
// the callback again rather than replacing it, so replaying blindly onto a
// live instance would fire every handler once per replay.
const appliedByInstance = new WeakMap<any, Set<EchoRegistration>>()

// The bound callback per (registration, Echo instance). `listen` binds the
// dedup wrapper, not `registration.handler`, so unregistering needs the
// wrapper to unbind exactly this listener — `stopListening(event)` without a
// callback would unbind every handler registered for that event+channel.
const wrapperByRegistration = new WeakMap<EchoRegistration, Map<any, (payload: any) => void>>()

// Channels whose `pusher:subscription_error` handler is already bound —
// binding once per channel, not once per registration.
const errorBoundChannels = new WeakSet<any>()

// At-least-once dedup window for _event_id stamped by BroadcastPayload.
// Keyed per-handler (eventId + handler identity): two different consumers
// on the same channel (e.g. quotations list vs sales KPIs) must BOTH fire,
// while the same handler subscribed via dual-channel fan-out
// (store.{t} + store.{t}.{wh}) fires once.
const seenEventHandlerPairs = new Set<string>()
let handlerIdCounter = 0
const handlerIds = new WeakMap<(...args: any[]) => void, number>()
function handlerIdFor(fn: (...args: any[]) => void): number {
  let id = handlerIds.get(fn)
  if (id === undefined) {
    handlerIdCounter += 1
    id = handlerIdCounter
    handlerIds.set(fn, id)
  }
  return id
}

/**
 * Socket-outage tracking. Broadcast events emitted while the socket is down
 * are lost for good — RTK Query's refetchOnReconnect only reacts to browser
 * online/offline transitions, not to a Reverb-side drop while the network
 * stays up (backend restart, idle proxy cut, expired channel auth). The
 * first 'connected' after an outage notifies resync subscribers so mounted
 * views can refetch instead of staying stale until the next event.
 */
let sawSocketOutage = false
// ISO timestamp of when the current outage STARTED (null when healthy).
// Lets resync subscribers pull missed events with `since=` instead of
// blindly refetching everything (see realtimeResync + GET /api/realtime/missed).
let socketOutageSince: string | null = null
type ResyncCallback = () => void
const resyncSubscribers = new Set<ResyncCallback>()

export function onRealtimeResync(cb: ResyncCallback): () => void {
  resyncSubscribers.add(cb)
  return () => {
    resyncSubscribers.delete(cb)
  }
}

function notifyRealtimeResync(): void {
  resyncSubscribers.forEach((cb) => {
    try {
      cb()
    } catch {
      // One failing subscriber must not stop the others.
    }
  })
}

function applyRegistration(echo: any, registration: EchoRegistration, applied: Set<EchoRegistration>): void {
  try {
    const channel = echo.private(registration.channelName)
    // Per-handler dedup: the backend stamps every payload with _event_id
    // (see BroadcastPayload). Reconnect replays + duplicate deliveries would
    // otherwise double-invalidate and double-fetch.
    const dedupedHandler = (payload: any) => {
      try {
        const eventId = payload?._event_id
        if (typeof eventId === 'string' && eventId !== '') {
          // Same handler fn on two channels (dual fan-out) shares the key
          // and fires once; different handlers get different keys and both
          // fire. Channel intentionally excluded from the key.
          const pairKey = `${eventId}::h${handlerIdFor(registration.handler)}`
          if (seenEventHandlerPairs.has(pairKey)) return
          seenEventHandlerPairs.add(pairKey)
          // Evict lazily in a batch: trimming on every event above the cap
          // copied the whole set (Array.from, O(n)) per delivered message. Only
          // trim past a high-water mark, back down to the cap.
          if (seenEventHandlerPairs.size > 2000) {
            let toDrop = seenEventHandlerPairs.size - 1000
            for (const id of seenEventHandlerPairs) {
              if (toDrop <= 0) break
              seenEventHandlerPairs.delete(id)
              toDrop -= 1
            }
          }
        }
      } catch {
        // Dedup must never break delivery.
      }
      registration.handler(payload)
    }
    channel.listen(registration.eventName, dedupedHandler)

    // Remember the bound wrapper so unregister can unbind exactly this
    // listener (per instance — wrappers are rebuilt on every replay).
    let wrappers = wrapperByRegistration.get(registration)
    if (!wrappers) {
      wrappers = new Map()
      wrapperByRegistration.set(registration, wrappers)
    }
    wrappers.set(echo, dedupedHandler)

    // Channel-level auth failures (expired token, 403) never change the
    // socket state — the connection still reads 'connected' while every
    // channel is dead. Surface them and arm the resync so the next
    // successful (re)authorization refetches whatever went stale. When the
    // access token itself is expired, tear the socket down so the status
    // poller rebuilds it after a fresh login instead of sitting
    // live-but-unauthorized forever.
    if (!errorBoundChannels.has(channel)) {
      errorBoundChannels.add(channel)
      channel.on?.('pusher:subscription_error', (err: any) => {
        const status = err?.error?.status ?? err?.status
        // Permission denial (e.g. non-admin subscribed to
        // admin.system-resources) is not a socket outage — marking it as
        // one triggers a global resync that invalidates every realtime tag.
        const isAuthzDenial = (status === 403) && !isAccessTokenExpired()
        if (!isAuthzDenial) {
          if (!sawSocketOutage) {
            socketOutageSince = new Date().toISOString()
          }
          sawSocketOutage = true
        }
        if ((status === 401 || status === 403) && isAccessTokenExpired()) {
          console.error(
            `[Echo] channel authorization failed (expired token): ${registration.channelName} — disconnecting; re-login required.`,
            err?.error?.data ?? err,
          )
          disconnectEcho()
          window.dispatchEvent(new CustomEvent('echo:auth-expired', { detail: { channel: registration.channelName } }))
          return
        }
        console.error(
          `[Echo] channel authorization failed: ${registration.channelName}`,
          err?.error?.data ?? err,
        )
      })
    }

    applied.add(registration)
  } catch {
    // Echo mid-rebuild or channel not ready — left unapplied so the
    // 'connected' replay retries it.
  }
}

function replayRegistrations(echo: any): void {
  if (!echo || echoRegistrations.length === 0) return
  let applied = appliedByInstance.get(echo)
  if (!applied) {
    applied = new Set()
    appliedByInstance.set(echo, applied)
  }
  echoRegistrations.forEach((registration) => {
    if (!applied!.has(registration)) {
      applyRegistration(echo, registration, applied!)
    }
  })
}

/**
 * Subscribe a handler to a private channel and keep it alive across Echo
 * instance rebuilds (logout/login, tenant switch, disconnect+re-init).
 *
 * `eventName` is passed to `.listen()` exactly as given — follow the
 * existing convention and pass the broadcastAs name with the leading dot
 * (e.g. '.SaleCreated').
 *
 * Returns an unregister function for React cleanup. Unregistering stops the
 * handler on the live instance and leaves the channel once no registration
 * for it remains.
 */
export function registerEchoListener(
  channelName: string,
  eventName: string,
  handler: (payload: any) => void,
): () => void {
  const domains: Record<string, string> = {
    '.SaleCreated': 'sales', '.SaleUpdated': 'sales', '.SaleDeleted': 'sales',
    '.sale.return.created': 'sale-returns', '.PurchaseCreated': 'purchases',
    '.PurchaseUpdated': 'purchases', '.PurchaseDeleted': 'purchases',
    '.purchase.return.created': 'purchase-returns', '.product.created': 'inventory',
    '.product.updated': 'inventory', '.product.deleted': 'inventory',
    '.stock.updated': 'inventory', '.LowStockDetected': 'inventory',
  }
  const store = /^store\.([^.]+)(?:\.(\d+))?$/.exec(channelName)
  if (store && domains[eventName]) {
    channelName = 'tenant.' + store[1] + '.store-events.' + domains[eventName]
      + (store[2] ? '.warehouse.' + store[2] : '')
  }
  const registration: EchoRegistration = { channelName, eventName, handler }
  echoRegistrations.push(registration)

  const echo = typeof window !== 'undefined' ? window.Echo : null
  if (echo) {
    replayRegistrations(echo)
  }

  return () => unregisterEchoListener(registration)
}

function unregisterEchoListener(registration: EchoRegistration): void {
  const index = echoRegistrations.indexOf(registration)
  if (index === -1) return
  echoRegistrations.splice(index, 1)

  const echo = typeof window !== 'undefined' ? window.Echo : null
  if (!echo) return

  try {
    // Only touch the channel if the instance actually has it — `echo.private()`
    // would SUBSCRIBE a brand-new channel (and fire /broadcasting/auth) just
    // to unregister from it.
    const key = `private-${registration.channelName}`
    const channels = echo.connector?.channels
    if (channels && !channels[key]) return

    const channel = channels ? channels[key] : echo.private(registration.channelName)
    // Unbind exactly this handler — the bound callback is the dedup wrapper
    // created in applyRegistration, not registration.handler.
    const wrapper = wrapperByRegistration.get(registration)?.get(echo)
    if (wrapper) {
      channel.stopListening(registration.eventName, wrapper)
      wrapperByRegistration.get(registration)?.delete(echo)
    } else {
      channel.stopListening(registration.eventName)
    }

    // When the last listener on a channel goes, fully leave it —
    // stopListening alone keeps the channel authorized and subscribed
    // forever. `Echo.leave(name)` resolves the internal `private-` prefixed
    // channel key; `leaveChannel(name)` does NOT (its map lookup misses) and
    // would silently keep the subscription alive.
    if (!echoRegistrations.some((r) => r.channelName === registration.channelName)) {
      echo.leave(registration.channelName)
    }
  } catch {
    // Echo may already be gone — nothing to do.
  }
}

/**
 * Why realtime is not running, when it is not. Build-time configuration
 * failures have no UI symptom of their own, so the reason is published here
 * and surfaced through useEcho (the single owner of connection state) instead
 * of living only in a console message.
 */
export type RealtimeDisabledReason =
  | 'not-enabled'
  | 'no-token'
  | 'missing-config'
  | 'bootstrap-error'
  | null

let disabledReason: RealtimeDisabledReason = null

export function getRealtimeDisabledReason(): RealtimeDisabledReason {
  return disabledReason
}

/**
 * True when a token expiry timestamp is stored and already passed (with a
 * 30s clock-skew margin). The Echo auth headers snapshot the token at
 * initEcho time, so an expired token makes every /broadcasting/auth 403
 * while pusher stays 'connected' — this is the self-heal trigger.
 */
export function isAccessTokenExpired(): boolean {
  try {
    const expiry = getTokenExpiry()
    if (expiry === null || Number.isNaN(Number(expiry))) return false
    return Date.now() > Number(expiry) - 30_000
  } catch {
    return false
  }
}

/**
 * Idempotent: creates the Echo client once per token+tenant pair. Safe to
 * call at app boot and again after login/tenant switch. Never throws — a
 * failed realtime bootstrap must not break auth flows.
 */
export function initEcho(): Echo<any> | null {
  if (typeof window === 'undefined') return null
  if (!REALTIME_FEATURES.enabled) {
    disabledReason = 'not-enabled'
    return null
  }

  try {
    const token = getAccessToken()
    if (!token) {
      // A token cleared without an explicit disconnect (logout, failed
      // re-init) must not leave a stale live socket on window.Echo while we
      // report realtime disabled — getEchoInstanceId()/getEchoSocketId()
      // would keep pointing at a client whose channel auth is dead.
      if (window.Echo) {
        disconnectEcho()
      }
      disabledReason = 'no-token'
      return null
    }

    // Proactive expiry recovery: never build a socket with a dead token —
    // every channel auth would 403 while the socket reads 'connected'.
    if (isAccessTokenExpired()) {
      // Same teardown as above (mirrors the expired-token handler bound in
      // applyRegistration): the reported state must match reality.
      if (window.Echo) {
        disconnectEcho()
      }
      disabledReason = 'no-token'
      console.error('[Echo] access token expired — realtime paused until re-login refreshes the token.')
      window.dispatchEvent(new CustomEvent('echo:auth-expired', { detail: { reason: 'token-expired-at-init' } }))
      return null
    }

    const tenantId = getTenantId()
    const sessionHeaders = browserAuthHeaders(token)
    // Cookie sessions keep the same nonsecret marker when a credential rotates.
    // Include its CSRF binding so reconnects do not retain the previous headers.
    const identity = `${token}:${tenantId ?? ''}:${sessionHeaders['X-POS-CSRF'] ?? ''}`
    if (window.Echo && bootstrappedIdentity === identity) {
      return window.Echo
    }

    disconnectEcho()

    const { key, host, port, secure } = REALTIME_FEATURES.websocket

    // A missing or un-substituted value (the .env.production placeholders are
    // shaped like "<public wss host>") makes every realtime feature silently
    // no-op. This is a build-time configuration error, not a network
    // condition, and nothing else in production would reveal it.
    const isPlaceholder = (value: string) => !value || value.includes('<') || value.includes('>') || value.includes('${')
    if (isPlaceholder(key) || isPlaceholder(host)) {
      disabledReason = 'missing-config'
      console.error(
        '[Echo] Realtime disabled: VITE_REVERB_APP_KEY / VITE_REVERB_HOST are missing or still ' +
          'placeholders. Set literal values in .env.production and rebuild (npm run build).',
        { key: key || '(empty)', host: host || '(empty)' },
      )
      return null
    }

    // Browsers must reach the PUBLIC websocket endpoint. Port 8080 is Reverb's
    // internal bind (firewalled, reached only through the Nginx wss proxy), so
    // it must never be carried into a wss:// URL — the inevitable result is a
    // connection to a port nothing listens on.
    const INTERNAL_REVERB_PORT = 8080
    const portIsInternalOnly = port === INTERNAL_REVERB_PORT
    if (secure && portIsInternalOnly) {
      console.error(
        `[Echo] VITE_REVERB_PORT=${INTERNAL_REVERB_PORT} is Reverb's internal bind and is not ` +
          'reachable from browsers behind the Nginx wss proxy — falling back to 443. ' +
          'Set VITE_REVERB_PORT=443 and rebuild.',
      )
    }
    const effectivePort = secure && portIsInternalOnly ? 443 : port

    // laravel-echo's reverb connector resolves the Pusher client from the
    // global scope (the official Laravel bootstrap pattern).
    window.Pusher = Pusher

    const echo = new Echo({
      broadcaster: 'reverb',
      key,
      wsHost: host,
      wsPort: effectivePort || 80,
      wssPort: effectivePort || 443,
      forceTLS: secure,
      enabledTransports: ['ws', 'wss'],
      disableStats: true,
      authEndpoint: '/broadcasting/auth',
      auth: {
        headers: {
          Accept: 'application/json',
          ...sessionHeaders,
          ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
        },
      },
    })

    window.Echo = echo
    bootstrappedIdentity = identity
    disabledReason = null
    currentInstanceId = ++instanceSeq

    // Surface channel-auth/socket errors instead of dying silently: an
    // expired Passport token makes every (re)authorization fail with no
    // other symptom than dead realtime. Pusher-js logs to console in DEV;
    // in production this makes the failure discoverable in telemetry.
    try {
      const connection = (echo as any).connector?.pusher?.connection
      connection?.bind('error', (err: any) => {
        if (import.meta.env.DEV) {
          console.warn('[Echo] connection error:', err?.error?.data ?? err)
        }
      })
      // A socket that drops and comes back must carry the registered channel
      // listeners with it (replay is per-instance deduplicated, so this is a
      // no-op when the subscriptions survived) — and after an actual outage
      // it must also trigger a data resync: every event broadcast while the
      // socket was down was lost.
      connection?.bind('connected', () => {
        replayRegistrations(echo)
        if (sawSocketOutage) {
          sawSocketOutage = false
          notifyRealtimeResync()
          // Keep socketOutageSince until subscribers have read it via
          // getAndClearRealtimeOutageSince() — clearing here would race them.
        }
      })
      const markOutage = () => {
        if (!sawSocketOutage) {
          socketOutageSince = new Date().toISOString()
        }
        sawSocketOutage = true
      }
      connection?.bind('disconnected', markOutage)
      connection?.bind('unavailable', markOutage)
      // Pusher can move straight from connected to connecting on a dropped
      // transport. A fast reconnect never emits disconnected/unavailable,
      // but events sent in that interval still need recovery.
      connection?.bind('state_change', (change: { previous?: string; current?: string }) => {
        if (change.previous === 'connected' && change.current !== 'connected') {
          markOutage()
        }
      })
    } catch {
      // Connector not built yet — Echo connects lazily on first subscribe.
    }

    // Replay every registered listener onto the fresh instance — the rebuild
    // that orphaned subscriptions before the registry existed.
    replayRegistrations(echo)

    return echo
  } catch (err) {
    disabledReason = 'bootstrap-error'
    console.error('[Echo] Realtime bootstrap failed — continuing without it.', err)
    return null
  }
}

export function disconnectEcho(): void {
  if (typeof window === 'undefined') return

  if (window.Echo) {
    try {
      window.Echo.disconnect()
    } catch {
      // Socket may already be closed — nothing to do.
    }
  }

  window.Echo = null
  bootstrappedIdentity = null
  disabledReason = null
  currentInstanceId = null
  // A deliberate teardown (logout/tenant switch) is not an outage — the
  // rebuilt instance's first connect must not fire a resync.
  sawSocketOutage = false
  socketOutageSince = null
}

/**
 * Current Pusher socket id for X-Socket-ID (toOthers exclusion).
 * Returns null when no live socket exists — callers must omit the header.
 */
export function getEchoSocketId(): string | null {
  try {
    const echo = typeof window !== 'undefined' ? (window as any).Echo : null
    const socketId = echo?.socketId?.() ?? echo?.connector?.pusher?.connection?.socket_id ?? null
    return typeof socketId === 'string' && socketId !== '' ? socketId : null
  } catch {
    return null
  }
}

/**
 * ISO timestamp of when the last socket outage STARTED, or null when the
 * socket is healthy. Consuming (clears after read) so a second resync
 * without a new outage does not re-pull the same window.
 */
export function getAndClearRealtimeOutageSince(): string | null {
  const since = socketOutageSince
  socketOutageSince = null
  return since
}

/**
 * Store channel fan-out for multi-store tenants. The backend broadcasts
 * warehouse-bearing events to BOTH `store.{tenant}` (legacy — every existing
 * subscriber listens here) and `store.{tenant}.{warehouse}`. Subscribe to
 * both: the legacy channel keeps global list views working, the scoped one
 * lets a till listen only to its own warehouse.
 */
export function storeChannelNames(tenantId: string | number | null | undefined, warehouseId?: string | number | null): string[] {
  if (tenantId === null || tenantId === undefined || String(tenantId) === '') return []
  const channels = [`store.${tenantId}`]
  if (warehouseId !== null && warehouseId !== undefined && String(warehouseId) !== '') {
    const scoped = `store.${tenantId}.${warehouseId}`
    if (!channels.includes(scoped)) channels.push(scoped)
  }
  return channels
}
